// src/components/PromoBanner.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

export default function PromoBanner({ promo }) {
  const isDark = promo?.theme === "dark";
  const isInternal = typeof promo?.ctaHref === "string" && promo.ctaHref.startsWith("/");

  const bgStyle = useMemo(() => {
    const gradient = promo?.gradient;
    const hasGradient = Array.isArray(gradient) && gradient.length === 2;

    if (hasGradient) {
      return { background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)` };
    }
    return {
      background: isDark
        ? "linear-gradient(135deg,#0b1220,#111827)"
        : "linear-gradient(135deg,#fff,#f8fafc)",
    };
  }, [promo?.gradient, isDark]);

  if (!promo) return null;

  const {
    title,
    subtitle,
    ctaLabel = "Voir",
    ctaHref = "/catalogue",
    imageUrl,
    badge,
    align = "left",
  } = promo;

  const Btn = isInternal ? Link : "a";
  const btnProps = isInternal
    ? { to: ctaHref }
    : { href: ctaHref, target: "_blank", rel: "noopener noreferrer" };

  return (
    <section className={`mb-3 mb-md-4 promo-wrap ${isDark ? "text-white" : "text-dark"}`}>
      <div
        className="promo-card rounded-4 overflow-hidden shadow-sm"
        style={bgStyle}
        aria-label={title || "Bannière promotionnelle"}
      >
        <div className="row g-0 align-items-stretch">
          {/* Media */}
          <div className="col-12 col-md-5 col-lg-4">
            <div className="promo-media">
              {imageUrl ? (
                <img src={imageUrl} alt={title || "Promotion"} loading="lazy" decoding="async" />
              ) : (
                <div className="promo-skeleton" aria-hidden="true" />
              )}
              <div className="promo-sheen" aria-hidden="true" />
              {badge ? (
                <span className={`promo-badge ${isDark ? "promo-badge-dark" : "promo-badge-light"}`}>
                  {badge}
                </span>
              ) : null}
            </div>
          </div>

          {/* Content */}
          <div className="col-12 col-md-7 col-lg-8">
            <div className={`promo-body p-3 p-md-4 p-lg-5 ${align === "center" ? "text-center" : ""}`}>
              {title ? <h2 className="promo-title mb-2">{title}</h2> : null}
              {subtitle ? <p className={`promo-sub mb-3 ${isDark ? "opacity-85" : "text-muted"}`}>{subtitle}</p> : null}

              <div className={`d-flex flex-wrap gap-2 ${align === "center" ? "justify-content-center" : ""}`}>
                <Btn
                  {...btnProps}
                  className={`btn btn-sm fw-semibold px-3 py-2 promo-cta ${isDark ? "btn-light" : "btn-dark"}`}
                >
                  {ctaLabel} <span aria-hidden="true">→</span>
                </Btn>

                <Link
                  to="/catalogue"
                  className={`btn btn-sm px-3 py-2 ${isDark ? "btn-outline-light" : "btn-outline-dark"}`}
                >
                  Catalogue
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .promo-card{
          position: relative;
          border: 1px solid rgba(255,255,255,.14);
          transition: transform .18s ease, box-shadow .18s ease;
          will-change: transform;
        }
        .promo-card:hover{
          transform: translateY(-2px);
          box-shadow: 0 14px 34px rgba(0,0,0,.16);
        }

        .promo-media{
          position: relative;
          min-height: 190px;
          height: 100%;
          background: rgba(255,255,255,.12);
          overflow: hidden;
        }
        .promo-media img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transform: scale(1.02);
          transition: transform .25s ease;
        }
        .promo-card:hover .promo-media img{ transform: scale(1.06); }

        .promo-skeleton{
          width: 100%;
          height: 100%;
          min-height: 190px;
          background: linear-gradient(90deg,#f1f5f9 0%,#e2e8f0 40%,#f1f5f9 80%);
          background-size: 200% 100%;
          animation: shimmer 1.2s infinite linear;
        }
        @keyframes shimmer{
          0%{ background-position: 200% 0; }
          100%{ background-position: -200% 0; }
        }

        .promo-sheen{
          position:absolute; inset:-40%;
          background: radial-gradient(circle at 30% 20%, rgba(255,255,255,.22), rgba(255,255,255,0) 55%);
          pointer-events:none;
          transform: rotate(8deg);
        }

        .promo-badge{
          position:absolute;
          top: 12px; left: 12px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 800;
          font-size: .78rem;
          letter-spacing: .2px;
          backdrop-filter: blur(6px);
        }
        .promo-badge-light{
          background: rgba(255,255,255,.72);
          color: #111827;
          border: 1px solid rgba(17,24,39,.10);
        }
        .promo-badge-dark{
          background: rgba(17,24,39,.55);
          color: #fff;
          border: 1px solid rgba(255,255,255,.18);
        }

        .promo-title{
          font-weight: 900;
          letter-spacing: -.02em;
          line-height: 1.15;
          font-size: clamp(1.1rem, 2.2vw, 1.6rem);
        }
        .promo-sub{
          font-size: .95rem;
          max-width: 52ch;
        }

        .promo-cta{
          border-radius: 999px;
          box-shadow: 0 10px 24px rgba(0,0,0,.12);
        }

        @media (max-width: 767.98px){
          .promo-media{ min-height: 42vw; }
          .promo-sub{ max-width: 100%; }
        }
      `}</style>
    </section>
  );
}
